/**
 * useVisitorWebSocket — drop-in replacement for useOperationalVisitors.
 *
 * Connects to the condo-manager WebSocket endpoint and receives real-time
 * visitor updates via JSON-RPC 2.0 notifications.
 *
 * Falls back to polling (useOperationalVisitors) if the WS connection fails
 * after MAX_WS_FAILURES consecutive attempts.
 *
 * Return shape is identical to useOperationalVisitors so no changes are
 * required in consuming components.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiBaseUrl, makeAbsolutePhotoUrl } from '../services/api';
import { logger } from '../services/logger';
import { hasPersonPhoto, setPersonPhoto } from '../utils/personPhotoCache';
import { toThumbnailUrl } from '../utils/thumbnailUrl';
import {
  VisitorWebSocketClient,
  WsVisitorData,
  WsVisitorSummary,
  WsVisitorPagination,
  WsCustomerNote,
  ConnectionStatus,
  WsStats,
} from '../services/visitorWebSocketClient';
import { OperationalVisitor } from './useOperationalVisitors';
import { CustomerNote } from './useCustomerNotes';

export type { WsStats } from '../services/visitorWebSocketClient';
// Re-export OperationalVisitor so callers don't need to change imports
export type { OperationalVisitor } from './useOperationalVisitors';

// ─── Return type (identical to useOperationalVisitors) ───────────────────────

interface PaginationInfo {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Cheap positional comparison of the fields that actually change on a note
// (conclude/reopen), instead of JSON.stringify-ing the whole array per WS push.
const notesEqual = (a: WsCustomerNote[], b: WsCustomerNote[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.id !== y.id ||
      x.is_active !== y.is_active ||
      x.content !== y.content ||
      x.concluded_at !== y.concluded_at
    ) {
      return false;
    }
  }
  return true;
};

export interface UseVisitorWebSocketResult {
  visitors: OperationalVisitor[];       // Current page
  allVisitors: OperationalVisitor[];    // All visitors (for exit detection & counts)
  notes: CustomerNote[];                // All active customer notes (replaces polling)
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
  lastRefreshTime: number;
  pagination: PaginationInfo;
  setPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  connectionStatus: ConnectionStatus;   // Bonus: expose WS state
  getWsStats: () => WsStats | null;     // Read current WS stats without causing re-renders
  setStatusFilter: (filter: string) => void; // Server-side status filter
  setSearchMode: (enabled: boolean) => void;  // Request all visitors (large page) for client-side search
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toOperationalVisitor(v: WsVisitorData): OperationalVisitor {
  const resolvedPhoto = makeAbsolutePhotoUrl(v.photo || null);

  // Cache the full-res URL so the person modal can access it.
  // Always overwrite if we have a real URL — a stale null from another source
  // (e.g. useGlobalEventStream) must not block the modal from using this photo.
  if (resolvedPhoto) {
    setPersonPhoto(v.personId, resolvedPhoto);
  } else if (!hasPersonPhoto(v.personId)) {
    setPersonPhoto(v.personId, null);
  }

  return {
    id: v.personId,         // matches OperationalVisitor.id (person ID)
    name: v.name,
    unit: v.unit,
    unit_id: v.unit_id,
    street_name: v.street_name,
    vehicle: v.vehicle,
    entryTime: v.entryTime,
    exitTime: v.exitTime,
    status: v.status as OperationalVisitor['status'],
    photo: toThumbnailUrl(resolvedPhoto),
    personId: v.personId,
    roleId: v.roleId,
    roleType: v.roleType as OperationalVisitor['roleType'],
    accessType: v.access_type,
    periodAccess: v.period_access === true,
    liberacao: v.liberacao,
    entry_window_expires_at: v.entry_window_expires_at,
    access_expires_at: v.access_expires_at,
    valid_until: v.valid_until,
    first_entry_at: v.first_entry_at,
    last_entry_at: v.last_entry_at,
    last_exit_at: v.last_exit_at,
    role_created_at: v.role_created_at,
    responsible_person_id: v.responsible_person_id,
    authorized_by_name: v.authorized_by_name,
  };
}

function summaryToOperationalVisitor(s: WsVisitorSummary): OperationalVisitor {
  return {
    id: s.personId,
    name: '',
    unit: '—',
    vehicle: '—',
    entryTime: '—',
    status: s.status as OperationalVisitor['status'],
    photo: null,
    personId: s.personId,
    roleId: s.roleId,
    roleType: 'visitor',
    first_entry_at: s.first_entry_at,
    last_entry_at: s.last_entry_at,
    last_exit_at: s.last_exit_at,
    entry_window_expires_at: s.entry_window_expires_at,
  };
}

function buildWsUrl(customerId: string): string {
  // getApiBaseUrl() returns e.g. "https://access.panther.com.br"
  const base = getApiBaseUrl();
  // Convert http(s) → ws(s)
  const wsBase = base.replace(/^http/, 'ws');
  return `${wsBase}/api/v1/customers/${customerId}/ws/visitors`;
}

function getToken(): string {
  // The api.ts setApiToken() stores the token in localStorage under 'token'
  return localStorage.getItem('token') || '';
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const MAX_WS_FAILURES = 3;

export function useVisitorWebSocket(
  customerId: string | null,
  _autoRefreshInterval = 0, // ignored — WS pushes updates
): UseVisitorWebSocketResult {
  const [visitors, setVisitors] = useState<OperationalVisitor[]>([]);
  const [allVisitors, setAllVisitors] = useState<OperationalVisitor[]>([]);
  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0, page: 1, pageSize: 50, totalPages: 0,
  });
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');

  const wsRef = useRef<VisitorWebSocketClient | null>(null);
  const customerIdRef = useRef<string | null>(null);
  const wsFailures = useRef(0);
  const hasLoadedRef = useRef(false);
  const pageRef = useRef(1);
  const pageSizeRef = useRef(50);
  const statusFilterRef = useRef('');

  // ── Apply an incoming WS update to component state ──────────────────────
  const applyUpdate = useCallback((
    wsVisitors: WsVisitorData[],
    wsSummary: WsVisitorSummary[],
    wsPagination: WsVisitorPagination,
    wsNotes: WsCustomerNote[],
  ) => {
    const mapped = wsVisitors.map(toOperationalVisitor);
    const allMapped = wsSummary.map(summaryToOperationalVisitor);

    // Smart-merge: keep existing object references if data hasn't changed
    setVisitors(prev => {
      if (prev.length === 0) return mapped;
      const existing = new Map(prev.map(v => [v.roleId, v]));
      return mapped.map(nv => {
        const old = existing.get(nv.roleId);
        if (!old) return nv;
        const changed =
          old.status !== nv.status ||
          old.entryTime !== nv.entryTime ||
          old.exitTime !== nv.exitTime ||
          old.name !== nv.name ||
          old.unit !== nv.unit ||
          old.unit_id !== nv.unit_id ||
          old.photo !== nv.photo;
        return changed ? nv : old;
      });
    });

    setAllVisitors(allMapped);

    // Smart-merge notes: only update if content changed (cheap field comparison,
    // avoids O(n) JSON.stringify on every WS push)
    setNotes(prev => {
      if (!notesEqual(prev, wsNotes)) return wsNotes as CustomerNote[];
      return prev;
    });

    setPagination({
      total: wsPagination.total,
      page: wsPagination.page,
      pageSize: wsPagination.pageSize,
      totalPages: wsPagination.totalPages || 1,
    });
    setLastRefreshTime(Date.now());
    setIsLoading(false);
    hasLoadedRef.current = true;
    setError(null);
  }, []);

  // ── Connect / reconnect WS ───────────────────────────────────────────────
  const connectWs = useCallback((custId: string) => {
    if (wsRef.current) {
      wsRef.current.disconnect();
    }

    const token = getToken();
    if (!token) {
      logger.warn('useVisitorWebSocket', 'No auth token found — cannot connect');
      setError(new Error('Auth token not available'));
      return;
    }

    const client = new VisitorWebSocketClient();
    wsRef.current = client;

    client.onStatusChange((status) => {
      setConnectionStatus(status);

      if (status === 'connected') {
        wsFailures.current = 0;
        // Subscribe with current page and active filter
        client.subscribe(pageRef.current, pageSizeRef.current, statusFilterRef.current);
      }

      if (status === 'reconnecting') {
        wsFailures.current += 1;
        if (wsFailures.current >= MAX_WS_FAILURES) {
          logger.warn('useVisitorWebSocket', `WS failed ${wsFailures.current}x — giving up`);
          client.disconnect();
          setError(new Error('WebSocket connection failed repeatedly'));
        }
      }
    });

    client.onUpdate((data) => {
      applyUpdate(
        data.visitors ?? [],
        data.allSummary ?? [],
        data.pagination,
        data.notes ?? []
      );
    });

    const wsUrl = buildWsUrl(custId);
    logger.info('useVisitorWebSocket', `Connecting to ${wsUrl}`);

    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }

    client.connect(wsUrl, token);
  }, [applyUpdate]);

  // ── Effect: connect on customerId change ─────────────────────────────────
  useEffect(() => {
    if (!customerId) {
      wsRef.current?.disconnect();
      wsRef.current = null;
      setVisitors([]);
      setAllVisitors([]);
      setNotes([]);
      hasLoadedRef.current = false;
      wsFailures.current = 0;
      setConnectionStatus('disconnected');
      setPagination({ total: 0, page: 1, pageSize: 50, totalPages: 0 });
      return;
    }

    // Reset on customer change
    if (customerIdRef.current !== customerId) {
      hasLoadedRef.current = false;
      wsFailures.current = 0;
      setCurrentPage(1);
      pageRef.current = 1;
      customerIdRef.current = customerId;
    }

    connectWs(customerId);

    return () => {
      wsRef.current?.disconnect();
      wsRef.current = null;
    };
  }, [customerId, connectWs]);

  // ── Page change: tell the server ─────────────────────────────────────────
  const setPage = useCallback((page: number) => {
    pageRef.current = page;
    setCurrentPage(page);
    const ws = wsRef.current;
    if (ws && ws.status === 'connected' && customerId) {
      ws.paginate(page, pageSizeRef.current, statusFilterRef.current);
    }
  }, [customerId]);

  const nextPage = useCallback(() => {
    setPage(Math.min(pageRef.current + 1, pagination.totalPages));
  }, [pagination.totalPages, setPage]);

  const prevPage = useCallback(() => {
    setPage(Math.max(pageRef.current - 1, 1));
  }, [setPage]);

  // ── Status filter change: reset to page 1 and re-subscribe ───────────────
  const setStatusFilter = useCallback((filter: string) => {
    statusFilterRef.current = filter;
    pageRef.current = 1;
    setCurrentPage(1);
    const ws = wsRef.current;
    if (ws && ws.status === 'connected' && customerId) {
      ws.subscribe(1, pageSizeRef.current, filter);
    }
  }, [customerId]);

  // ── Search mode: request all visitors at once for client-side filtering ──
  const setSearchMode = useCallback((enabled: boolean) => {
    pageSizeRef.current = enabled ? 1000 : 50;
    pageRef.current = 1;
    setCurrentPage(1);
    const ws = wsRef.current;
    if (ws && ws.status === 'connected' && customerId) {
      ws.subscribe(1, pageSizeRef.current, statusFilterRef.current);
    }
  }, [customerId]);

  // ── Stats: read from client ref without causing re-renders ───────────────
  const getWsStats = useCallback((): WsStats | null => {
    return wsRef.current?.getStats() ?? null;
  }, []);

  // ── Manual refresh: re-subscribe ─────────────────────────────────────────
  const refresh = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.status === 'connected') {
      ws.subscribe(pageRef.current, pageSizeRef.current, statusFilterRef.current);
    } else if (customerId) {
      connectWs(customerId);
    }
  }, [customerId, connectWs]);

  return {
    visitors,
    allVisitors,
    notes,
    isLoading,
    error,
    refresh,
    lastRefreshTime,
    pagination,
    setPage,
    nextPage,
    prevPage,
    setStatusFilter,
    setSearchMode,
    connectionStatus,
    getWsStats,
  };
}
